import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { makeApiRequest } from '../../Shared/Api';
import {
	JOB_STATUS_COLOR_OPTIONS,
	clearJobStatusCache,
} from '../../Hooks/useJobStatuses';
import { refreshOnboardingChecklist } from '../../Shared/onboarding';

const emptyDraft = () => ({
	id: null,
	slug: '',
	label: '',
	color: 'default',
});

const JobStatusesSettings = ({ onSuccess, onError }) => {
	const [drafts, setDrafts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState('');

	useEffect(() => {
		let active = true;
		setLoading(true);
		setLoadError('');
		makeApiRequest('GET', '/jobs/job-statuses/')
			.then((response) => {
				if (!active) {
					return;
				}
				setDrafts(
					(response || []).map((status) => ({
						id: status.id,
						slug: status.slug,
						label: status.label,
						color: status.color || 'default',
					})),
				);
			})
			.catch((error) => {
				if (active) {
					setLoadError(error.message || 'Failed to load job statuses.');
				}
			})
			.finally(() => {
				if (active) {
					setLoading(false);
				}
			});
		return () => {
			active = false;
		};
	}, []);

	const moveDraft = (index, direction) => {
		const target = index + direction;
		if (target < 0 || target >= drafts.length) {
			return;
		}
		setDrafts((prev) => {
			const next = [...prev];
			[next[index], next[target]] = [next[target], next[index]];
			return next;
		});
	};

	const updateDraft = (index, field, value) => {
		setDrafts((prev) =>
			prev.map((item, itemIndex) =>
				itemIndex === index ? { ...item, [field]: value } : item,
			),
		);
	};

	const removeDraft = (index) => {
		setDrafts((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
	};

	const addDraft = () => {
		setDrafts((prev) => [...prev, emptyDraft()]);
	};

	const handleSave = async () => {
		const trimmed = drafts.map((item) => ({
			...item,
			label: item.label.trim(),
		}));
		if (trimmed.length === 0) {
			onError?.('Add at least one job status.');
			return;
		}
		if (trimmed.some((item) => !item.label)) {
			onError?.('Each status needs a label.');
			return;
		}

		setSaving(true);
		try {
			const payload = {
				statuses: trimmed.map((item) => ({
					id: item.id || undefined,
					slug: item.slug.trim() || undefined,
					label: item.label,
					color: item.color || 'default',
				})),
			};
			const response = await makeApiRequest(
				'PUT',
				'/jobs/job-statuses/bulk/',
				payload,
			);
			setDrafts(
				(response || []).map((status) => ({
					id: status.id,
					slug: status.slug,
					label: status.label,
					color: status.color || 'default',
				})),
			);
			clearJobStatusCache();
			refreshOnboardingChecklist();
			onSuccess?.('Job statuses saved.');
		} catch (error) {
			onError?.(error.message || 'Failed to save job statuses.');
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return <Typography color="text.secondary">Loading statuses...</Typography>;
	}

	return (
		<Stack spacing={2}>
			{loadError ? <Alert severity="error">{loadError}</Alert> : null}
			<Typography variant="body2" color="text.secondary">
				Define the stages jobs move through in your shop. These appear on the
				job list and job detail pages.
			</Typography>

			<Stack spacing={1.5}>
				{drafts.map((draft, index) => (
					<Box
						key={draft.id || `new-${index}`}
						sx={{
							display: 'grid',
							gridTemplateColumns: {
								xs: '1fr',
								sm: 'minmax(0, 1fr) 140px auto',
							},
							gap: 1.5,
							alignItems: 'center',
							p: 1.5,
							border: 1,
							borderColor: 'divider',
							borderRadius: 1.5,
						}}
					>
						<TextField
							label="Label"
							value={draft.label}
							onChange={(event) =>
								updateDraft(index, 'label', event.target.value)
							}
							fullWidth
							size="small"
							inputProps={{ maxLength: 100 }}
						/>
						<FormControl size="small" fullWidth>
							<InputLabel id={`status-color-${index}`}>Color</InputLabel>
							<Select
								labelId={`status-color-${index}`}
								label="Color"
								value={draft.color}
								onChange={(event) =>
									updateDraft(index, 'color', event.target.value)
								}
								renderValue={(selected) => (
									<Chip
										label={
											JOB_STATUS_COLOR_OPTIONS.find(
												(option) => option.value === selected,
											)?.label || selected
										}
										color={selected}
										size="small"
									/>
								)}
							>
								{JOB_STATUS_COLOR_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										<Chip
											label={option.label}
											color={option.value}
											size="small"
										/>
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Stack direction="row" spacing={0.5} justifyContent="flex-end">
							<IconButton
								size="small"
								onClick={() => moveDraft(index, -1)}
								disabled={index === 0}
								aria-label="Move status up"
							>
								<ArrowUpwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								onClick={() => moveDraft(index, 1)}
								disabled={index === drafts.length - 1}
								aria-label="Move status down"
							>
								<ArrowDownwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								onClick={() => removeDraft(index)}
								disabled={drafts.length <= 1}
								aria-label="Remove status"
							>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</Stack>
					</Box>
				))}
			</Stack>

			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1.5}
				justifyContent="space-between"
			>
				<Button
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={addDraft}
					sx={{ width: { xs: '100%', sm: 'auto' } }}
				>
					Add status
				</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={saving}
					sx={{ width: { xs: '100%', sm: 'auto' } }}
				>
					{saving ? 'Saving...' : 'Save statuses'}
				</Button>
			</Stack>
		</Stack>
	);
};

export default JobStatusesSettings;
